'use client';

import type { CmsFooterLocaleSwitcherModel } from '@/cms/components/chrome/cms-footer-locale-switcher.types';
import { renderAppIcon } from '@/components/icons/app-icon-registry';
import { setLocaleOverrideCookie } from '@/lib/i18n/locale-preference';
import type { Locale } from 'next-intl';
import Link from 'next/link';
import { useEffect, useMemo, useRef, useState } from 'react';
import styles from './cms-site-footer.module.css';

export function CmsFooterLocaleSwitcher({
  model,
}: {
  model: CmsFooterLocaleSwitcherModel;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const searchInputRef = useRef<HTMLInputElement | null>(null);
  const normalizedQuery = query.trim().toLowerCase();
  const filteredLocaleOptions = useMemo(
    () =>
      model.options.filter((option) => {
        if (!normalizedQuery) {
          return true;
        }

        return (
          option.locale.toLowerCase().includes(normalizedQuery) ||
          option.label.toLowerCase().includes(normalizedQuery)
        );
      }),
    [model.options, normalizedQuery]
  );

  useEffect(() => {
    if (!open) {
      setQuery('');
      return;
    }

    const timer = window.setTimeout(() => {
      searchInputRef.current?.focus();
    }, 0);

    return () => window.clearTimeout(timer);
  }, [open]);

  return (
    <div className={styles.localeSwitcher}>
      <button
        type="button"
        className={styles.localeTrigger}
        aria-expanded={open}
        aria-haspopup="menu"
        aria-label={`${model.menuLabel}: ${model.currentLocaleLabel}`}
        onClick={() => setOpen((value) => !value)}
      >
        <span className={styles.localeTriggerContent}>
          {renderAppIcon({ name: 'globe', className: styles.localeIcon })}
          <span className={styles.localeLabel}>{model.currentLocaleLabel}</span>
        </span>
        {renderAppIcon({
          name: 'chevron-down',
          className: [
            styles.localeChevron,
            open ? styles.localeChevronOpen : '',
          ].join(' '),
        })}
      </button>

      <div
        className={[styles.localeMenu, open ? styles.localeMenuOpen : ''].join(
          ' '
        )}
      >
        <div className={styles.localeMenuInner} aria-label={model.menuLabel}>
          <div className={styles.localeSearch}>
            {renderAppIcon({
              name: 'search',
              className: styles.localeSearchIcon,
            })}
            <input
              ref={searchInputRef}
              id="cms-footer-locale-search"
              name="cms-footer-locale-search"
              type="search"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder={model.searchPlaceholder}
              aria-label={model.searchPlaceholder}
              className={styles.localeSearchInput}
            />
          </div>

          <div className={styles.localeOptions}>
            {filteredLocaleOptions.length > 0 ? (
              filteredLocaleOptions.map((option) => (
                <Link
                  key={option.locale}
                  href={option.href}
                  prefetch={false}
                  className={[
                    styles.localeOption,
                    option.isCurrent ? styles.localeOptionActive : '',
                  ].join(' ')}
                  onClick={() =>
                    setLocaleOverrideCookie(option.locale as Locale)
                  }
                >
                  <span>{option.label}</span>
                </Link>
              ))
            ) : (
              <div className={styles.localeEmpty}>{model.emptyStateLabel}</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
